import { h, Component, Host, Prop, Element } from '@stencil/core';

import type { TableStateInterface } from '../../../../services/interfaces';
import { isNumber } from '../../../../services/utils';
import { CONTENT_ALIGNMENT } from '../helpers/constants';
import { getTableState } from '../helpers/table-state';

@Component({
  tag: 'pk-table-cell',
  styleUrl: './scss/pk-table-cell.scss',
  scoped: true,
})
export class PkTableCell {
  // Own Properties
  // ====================

  private tableState: TableStateInterface;

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkTableCellElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Column key identifier */
  @Prop({ reflect: true }) columnKey: string | number;

  /** Displays right side border if true */
  @Prop({ reflect: true }) withDivider?: boolean;

  /** Changes the cell content alignment. */
  @Prop({ reflect: true }) aligned?: string = 'left';

  /** Displays the row index, if provided. For single digits indexes, 0 will be prepended visually.*/
  @Prop({ reflect: true }) rowIndex?: string | number;

  /** Specify font size */
  @Prop({ reflect: true }) fontSize: number = 16;

  /** Specify if the cell should be highlighted. Any highlighted cell will be bold. */
  @Prop({ reflect: true }) highlight?: boolean;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    const tableId = this.el.closest<HTMLPkTableElement>('pk-table[table-id]')?.tableId;
    this.tableState = getTableState(tableId);
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // ==========================

  private get cellAlignment() {
    if (this.aligned && !CONTENT_ALIGNMENT.includes(this.aligned)) {
      console.warn(`[pk-table-cell]: 'aligned' property should be one of ${String(CONTENT_ALIGNMENT)}`);
      return {};
    }
    if (this.aligned === 'center') {
      return { ['--pk-cell--justify-content']: 'center', ['--pk-cell--text-align']: 'center' };
    }
    if (this.aligned === 'right') {
      return { ['--pk-cell--justify-content']: 'flex-end', ['--pk-cell--text-align']: 'right' };
    }
    return {};
  }

  private get cellVerticalPad() {
    if (!this.tableState) return {};

    const { cellVerticalPad } = this.tableState;

    if (cellVerticalPad < 4 || cellVerticalPad % 4 !== 0) {
      console.warn('Cell padding minimum is 4px, and must be a multiple of 4.');
      return {};
    }
    if (!cellVerticalPad) {
      return {};
    }

    return { ['--pk-cell--vertical-pad']: `${cellVerticalPad}px` };
  }

  private get cellHorizontalPad() {
    if (!this.tableState) return {};

    const { cellHorizontalPad } = this.tableState;
    if (cellHorizontalPad < 8 || cellHorizontalPad % 4 !== 0) {
      console.warn('Cell padding minimum is 8px, and must be a multiple of 4.');
      return {};
    }

    if (!cellHorizontalPad) {
      return {};
    }

    return { ['--pk-cell--horizontal-pad']: `${cellHorizontalPad}px` };
  }

  private get colAttributes() {
    if (!this.tableState) return {};

    const { cols, sortBy } = this.tableState;
    if (!cols?.length) return {};

    const colAttributes = cols.find((col) => col.columnKey === this.columnKey);
    if (!colAttributes) return {};

    const { isHidden, width, usePercentageWidth, canGrow, canShrink } = colAttributes;

    return { isHidden, width, usePercentageWidth, sortBy, canGrow, canShrink };
  }

  // 1render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const { isHidden, width, usePercentageWidth, sortBy, canShrink, canGrow } = this.colAttributes;

    const style = {
      ...(this.fontSize && { '--pk-cell--font-size': `${this.fontSize}px` }),
      ...(isNumber(width) && { '--pk-cell--width': usePercentageWidth ? `${width}%` : `${width}px` }),
      ...this.cellVerticalPad,
      ...this.cellHorizontalPad,
      ...this.cellAlignment,
    };

    return (
      <Host
        aria-hidden={isHidden}
        class={{
          'pk-table--row-cell': true,
          'is-active': sortBy?.columnKey === this.columnKey,
          highlight: this.highlight,
          'with-divider': this.withDivider,
          'with-width': isNumber(width),
          'is-hidden': isHidden,
          'can-grow': !!canGrow,
          'can-shrink': !!canShrink,
        }}
        style={{ ...style }}
        role="gridcell"
      >
        {this.rowIndex && (
          <span class="row-index">{this.rowIndex.toString().length === 1 ? `0${this.rowIndex}` : this.rowIndex}</span>
        )}
        <slot />
      </Host>
    );
  }
}
